import React from "react";
import { Link } from "react-router-dom";
import { Cable, Network, Satellite, Zap, Check, ArrowRight } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { SERVICE_IMAGES } from "@/lib/company";

const icons = { fiber: Cable, network: Network, broadband: Satellite, electro: Zap };

export default function Services() {
  const { t, lang } = useLanguage();
  const { content } = useContent();
  const services = lang === "de" && content?.services ? content.services : t.services;

  return (
    <div data-testid="services-page">
      <PageHeader
        overline={t.servicesSection.overline}
        title={t.servicesSection.title}
        subtitle={t.servicesSection.subtitle}
      />

      <div className="bg-white">
        {services.map((s, i) => {
          const Icon = icons[s.key];
          const reversed = i % 2 === 1;
          return (
            <section
              key={s.key}
              id={s.key}
              className={`border-b border-[#E5E5E5] ${i % 2 === 1 ? "bg-[#FAFAFA]" : "bg-white"}`}
              data-testid={`service-${s.key}`}
            >
              <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 lg:grid-cols-2 lg:gap-16 lg:px-10 lg:py-24">
                <Reveal className={reversed ? "lg:order-2" : ""}>
                  <div className="aspect-[16/11] overflow-hidden border border-[#E5E5E5]">
                    <img src={SERVICE_IMAGES[s.key]} alt={s.title} className="h-full w-full object-cover" />
                  </div>
                </Reveal>
                <Reveal delay={0.08} className={reversed ? "lg:order-1" : ""}>
                  <div className="flex items-center gap-3">
                    <span className="flex h-12 w-12 items-center justify-center bg-[#0A0A0A]">
                      <Icon size={24} className="text-white" strokeWidth={1.5} />
                    </span>
                    <span className="text-sm font-semibold uppercase tracking-[0.2em] text-[#E8620E]">
                      0{i + 1}
                    </span>
                  </div>
                  <h2 className="mt-6 text-3xl font-semibold tracking-tight text-[#0A0A0A] md:text-4xl">
                    {s.title}
                  </h2>
                  <p className="mt-4 text-base leading-relaxed text-[#4A4A4A] md:text-lg">
                    {s.short}
                  </p>
                  <ul className="mt-7 space-y-3">
                    {s.points.map((p, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check size={20} className="mt-0.5 shrink-0 text-[#E8620E]" />
                        <span className="text-[#0A0A0A]">{p}</span>
                      </li>
                    ))}
                  </ul>
                </Reveal>
              </div>
            </section>
          );
        })}
      </div>

      {/* CTA */}
      <section className="bg-[#0A0A0A]">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 py-16 lg:flex-row lg:items-center lg:px-10 lg:py-20">
          <h2 className="max-w-2xl text-3xl font-semibold tracking-tight text-white md:text-4xl">
            {t.ctaBand.title}
          </h2>
          <Link
            to="/kontakt"
            data-testid="services-cta-button"
            className="group flex shrink-0 items-center gap-2 bg-[#E8620E] px-8 py-4 text-sm font-semibold text-white transition-colors duration-200 hover:bg-[#C4520C]"
          >
            {t.ctaBand.button}
            <ArrowRight size={18} className="transition-transform duration-200 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>
    </div>
  );
}
